import { makeAssistantToolUI } from '@assistant-ui/react';
import { PlaybookCard } from '../../../playbooks/components/playbook-cards/playbook-card';
import { ChevronRight } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { FC, useState } from 'react';
import { Button } from '@shadcn/ui/components/ui/button';
import { cn } from '@shadcn/ui/lib/utils';
import {
    createPlaybookToolSignature,
    displayPlaybooksToolSignature,
    getAllPlaybooksToolSignature,
    updatePlaybooksToolSignature,
} from '@andsend/services-agent-shared';
import { z } from 'zod';
import { GenericToolUI } from './generic-tool-ui';
import { PlaybookPreview } from '../../../action-feed/onboarding/components/first-playbook-card';
import { GET_PLAYBOOK_BY_GROUP_ID } from '@zaplify/graphql';
import { useQuery } from '@apollo/client';
import {
    ContentPartStatus,
    ToolCallContentPartStatus,
} from 'node_modules/@assistant-ui/react/dist/types/AssistantTypes';

type DisplayPlaybooksToolArgs = z.infer<typeof displayPlaybooksToolSignature.inputSchema>;
type DisplayPlaybooksToolResult = z.infer<typeof displayPlaybooksToolSignature.outputSchema>;

interface CardStackProps {
    cards: React.ReactNode[];
}

const CardStack = ({ cards }: CardStackProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % cards.length);
    };

    const goToPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    };

    const getCardStatus = (index: number): 'active' | 'inactive' | 'prev' | 'next' => {
        if (index === currentIndex) return 'active';
        const relativePosition = (index - currentIndex + cards.length) % cards.length;
        if (relativePosition === 1) return 'next';
        if (relativePosition === cards.length - 1) return 'prev';
        return 'inactive';
    };

    return (
        <div className="flex flex-col h-80 select-none w-full justify-center items-center">
            <div className="card-stack relative h-64 w-[500px] ml-[250px]">
                {cards.map((card, index) => (
                    <div
                        key={index}
                        className={cn('absolute inset-0 transition-all duration-300 h-64 w-64', {
                            'z-30 translate-x-0 opacity-100': getCardStatus(index) === 'active',
                            'z-20 translate-x-[calc(25%)] opacity-50 scale-90 cursor-pointer hover:opacity-75 hover:scale-[0.94] [&_button]:pointer-events-none [&_.playbook-link-image-container]:pointer-events-none':
                                getCardStatus(index) === 'next',
                            'z-10 -translate-x-[calc(25%)] opacity-50 scale-90 cursor-pointer hover:opacity-75 hover:scale-[0.94] [&_button]:pointer-events-none [&_.playbook-link-image-container]:pointer-events-none':
                                getCardStatus(index) === 'prev',
                            'z-0 translate-x-0 opacity-0 pointer-events-none': getCardStatus(index) === 'inactive',
                        })}
                        onClick={() => {
                            if (getCardStatus(index) === 'next') {
                                goToNext();
                            } else if (getCardStatus(index) === 'prev') {
                                goToPrev();
                            }
                        }}
                    >
                        {card}
                    </div>
                ))}
            </div>

            <div className="z-50 flex justify-center gap-4">
                <Button variant="ghost" size="icon" onClick={goToPrev}>
                    <ChevronLeft className="w-6 h-6" />
                </Button>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                        {currentIndex + 1} / {cards.length}
                    </span>
                </div>
                <Button variant="ghost" size="icon" onClick={goToNext}>
                    <ChevronRight className="w-6 h-6" />
                </Button>
            </div>
        </div>
    );
};

const PlaybookCardsUI = ({ result }: { result: DisplayPlaybooksToolResult }) => {
    if (result?.groupIds === undefined) {
        return null;
    }

    if (result.groupIds.length === 0) {
        return <div>No playbooks found</div>;
    }

    if (result.groupIds.length === 1) {
        return (
            <div className="flex w-full justify-center items-center my-2 animate-fade-in-0.2">
                <FullSizePlaybook key={result.groupIds[0]} groupId={result.groupIds[0]} />
            </div>
        );
    }

    return (
        <CardStack
            cards={result.groupIds.map((groupId) => (
                <PlaybookCard key={groupId} groupId={groupId} />
            ))}
        />
    );
};

const FullSizePlaybook: FC<{ groupId: string }> = ({ groupId }) => {
    const { data: playbook } = useQuery(GET_PLAYBOOK_BY_GROUP_ID, {
        variables: {
            groupId,
        },
    });

    if (!playbook) return null;

    return <PlaybookPreview playbook={playbook?.Groups[0]} />;
};

const CollapsiblePlaybooksList = <T extends Record<string, any>, U extends Record<string, any>>({
    groupIds,
    toolSignature,
    args,
    toolStatus,
}: {
    groupIds: string[];
    toolSignature: any;
    args: T;
    toolStatus: ContentPartStatus | ToolCallContentPartStatus;
}) => {
    return (
        <div className="w-full flex flex-col">
            <GenericToolUI toolSignature={toolSignature} args={args} status={toolStatus}>
                <div className="p-4">
                    <PlaybookCardsUI result={{ groupIds }} />
                </div>
            </GenericToolUI>
        </div>
    );
};

export const DisplayPlaybooksToolUI = makeAssistantToolUI<DisplayPlaybooksToolArgs, DisplayPlaybooksToolResult>({
    toolName: displayPlaybooksToolSignature.name,
    render: ({ args, result }) => PlaybookCardsUI({ result }),
});

export const GetAllPlaybooksToolUI = makeAssistantToolUI<
    z.infer<typeof getAllPlaybooksToolSignature.inputSchema>,
    z.infer<typeof getAllPlaybooksToolSignature.outputSchema>
>({
    toolName: getAllPlaybooksToolSignature.name,
    render: ({ args, result, status }) => {
        return (
            <CollapsiblePlaybooksList
                toolSignature={getAllPlaybooksToolSignature}
                args={args}
                toolStatus={status}
                groupIds={result?.playbooks?.map((playbook) => playbook?.group?.id).filter(Boolean) || []}
            />
        );
    },
});

export const CreatePlaybookToolUI = makeAssistantToolUI<
    z.infer<typeof createPlaybookToolSignature.inputSchema>,
    z.infer<typeof createPlaybookToolSignature.outputSchema>
>({
    toolName: createPlaybookToolSignature.name,
    render: ({ args, result, status }) => {
        if (result?.groupId) {
            return (
                <CollapsiblePlaybooksList
                    toolSignature={createPlaybookToolSignature}
                    args={args}
                    toolStatus={status}
                    groupIds={[result.groupId]}
                />
            );
        }

        return (
            <div className="flex flex-col gap-2 animate-fade-in-0.2">
                <GenericToolUI toolSignature={createPlaybookToolSignature} args={args} status={status} />
            </div>
        );
    },
});

export const UpdatePlaybookToolUI = makeAssistantToolUI<
    z.infer<typeof updatePlaybooksToolSignature.inputSchema>,
    z.infer<typeof updatePlaybooksToolSignature.outputSchema>
>({
    toolName: updatePlaybooksToolSignature.name,
    render: ({ args, result, status }) => {
        if (args.assistantSettingsId) {
            return (
                <CollapsiblePlaybooksList
                    toolSignature={updatePlaybooksToolSignature}
                    args={args}
                    toolStatus={status}
                    groupIds={[args.assistantSettingsId]}
                />
            );
        }

        return (
            <div className="flex flex-col gap-2 animate-fade-in-0.2">
                <GenericToolUI toolSignature={updatePlaybooksToolSignature} args={args} status={status} />
            </div>
        );
    },
});
